import type { Lesson } from './courses';

/**
 * Parcours 9 : Roadmap & Planification — 5 lecons.
 */
export const ROADMAP_PLANNING_LESSONS: Lesson[] = [
  {
    slug: 'roadmap-basics',
    title: "Qu'est-ce qu'une roadmap marketing ?",
    duration: '3 min',
    glossaryTerms: ['roadmap', 'milestone'],
    sections: [
      {
        type: 'concept',
        title: 'Le concept',
        content:
          "Une roadmap marketing est un document strategique qui planifie vos initiatives marketing dans le temps. Contrairement a un simple calendrier editorial, la roadmap couvre des objectifs plus larges : lancement produit, campagne de notoriete, expansion geographique, etc.\n\nElle se compose de :\n- **Jalons (Milestones)** — Points de repere temporels (ex: lancer le blog en mars)\n- **Categories** — Thematiques (contenu, SEO, campagne, sales, referral)\n- **Statuts** — Planifie, en cours, termine, bloque",
      },
      {
        type: 'why',
        title: "Pourquoi c'est important ?",
        content:
          "Sans roadmap, les equipes marketing travaillent en mode reactif, rebondissant d'une urgence a l'autre sans vision d'ensemble. Une roadmap claire aligne l'equipe sur les priorites et permet de dire non aux distractions.\n\n83% des equipes marketing performantes utilisent une roadmap formalisee (CoSchedule). Les equipes sans roadmap passent 50% de leur temps sur des taches non prioritaires.",
      },
      {
        type: 'methodology',
        title: 'Comment faire ?',
        content:
          "1. **Definissez vos objectifs trimestriels** — 3-5 objectifs maximum, mesurables\n2. **Decomposez en jalons** — Chaque objectif se traduit en 3-8 jalons concrets\n3. **Assignez des responsables** — Chaque jalon a un proprietaire\n4. **Fixez des dates limites** — Realistes mais ambitieuses\n5. **Revoyez chaque semaine** — 15 min de standup pour mettre a jour les statuts\n6. **Ajustez chaque mois** — La roadmap doit rester un outil vivant",
      },
      {
        type: 'in-product',
        title: 'Dans Feedback Orchestrator',
        content:
          "La page **Roadmap** vous permet de creer des feuilles de route avec des jalons, de les visualiser en timeline ou en kanban, et de les correler avec les modules Growth (Content Factory, Blog Manager, Campaigns, etc.).",
      },
    ],
    linkedModule: {
      label: 'Roadmap',
      path: '/growth/roadmap',
      icon: 'Map',
    },
  },
  {
    slug: 'milestones-okrs',
    title: 'Jalons, OKRs et objectifs SMART',
    duration: '4 min',
    glossaryTerms: ['milestone', 'kpi'],
    sections: [
      {
        type: 'concept',
        title: 'Le concept',
        content:
          "Trois frameworks pour definir et suivre vos objectifs :\n\n- **Jalons (Milestones)** — Points de repere dans une roadmap. Simples et visuels. \"Publier 10 articles SEO d'ici fin mars.\"\n- **OKRs (Objectives and Key Results)** — Objectif ambitieux + 3-5 resultats cles mesurables. Utilises par Google, Intel, Spotify.\n- **SMART** — Specifique, Mesurable, Atteignable, Realiste, Temporel. Le classique toujours efficace.\n\nChoisissez le framework le plus adapte a votre equipe. Pour les petites equipes, les jalons simples sont souvent suffisants.",
      },
      {
        type: 'why',
        title: "Pourquoi c'est important ?",
        content:
          "Les equipes qui definissent des objectifs clairs ont 2.5x plus de chances de les atteindre (MIT Sloan). Le framework OKR, popularise par John Doerr chez Google, est utilise par 60% des entreprises tech de la Silicon Valley.\n\nLa cle : des objectifs ambitieux mais mesurables, avec un suivi regulier.",
      },
      {
        type: 'methodology',
        title: 'Comment faire ?',
        content:
          "**Framework OKR :**\n- **Objective** : Devenir la reference en content marketing B2B (qualitatif, inspirant)\n- **Key Result 1** : Publier 12 articles de blog (4/mois)\n- **Key Result 2** : Atteindre 5 000 visiteurs organiques/mois\n- **Key Result 3** : Generer 50 leads via le blog\n\n**Framework SMART :**\n- **S**pecifique : Publier 12 articles de blog SEO\n- **M**esurable : 12 articles, 5 000 visiteurs\n- **A**tteignable : 4 articles/mois est realiste avec l'IA\n- **R**ealiste : Le blog est un canal prouve en B2B\n- **T**emporel : D'ici fin du trimestre",
      },
      {
        type: 'in-product',
        title: 'Dans Feedback Orchestrator',
        content:
          "Chaque jalon dans la **Roadmap** peut etre associe a une categorie (contenu, SEO, campagne, etc.) et a un module lie. Quand vous terminez un jalon, la barre de progression globale se met a jour automatiquement.",
      },
    ],
    linkedModule: {
      label: 'Roadmap',
      path: '/growth/roadmap',
      icon: 'Map',
    },
  },
  {
    slug: 'planning-launch',
    title: 'Planifier un lancement produit',
    duration: '4 min',
    glossaryTerms: ['campaigns', 'content-calendar', 'roadmap'],
    sections: [
      {
        type: 'concept',
        title: 'Le concept',
        content:
          "Un lancement produit reussi ne s'improvise pas. Il necessite une coordination entre contenu, campagnes, sales et customer success. La roadmap de lancement type couvre :\n\n- **J-30** : Preparation (contenu, landing page, sequences email)\n- **J-7** : Pre-lancement (teasing, invitations early access)\n- **Jour J** : Lancement (annonce, demo, campagne presse)\n- **J+7** : Post-lancement (suivi leads, feedbacks, iterations)\n- **J+30** : Bilan (resultats vs objectifs, next steps)",
      },
      {
        type: 'why',
        title: "Pourquoi c'est important ?",
        content:
          "55% des lancements produit echouent a cause d'un mauvais timing ou d'une preparation insuffisante (Product Marketing Alliance). Les lancements planifies avec une roadmap detaillee generent en moyenne 3x plus de leads que les lancements improvises.\n\nUn bon lancement necessite la coordination de 5 a 10 jalons sur 4 a 8 semaines.",
      },
      {
        type: 'methodology',
        title: 'Comment faire ?',
        content:
          "1. **Creez une roadmap de lancement** — Un jalon par grande etape (J-30, J-7, J, J+7, J+30)\n2. **Preparez le contenu a l'avance** — Articles, posts, emails, FAQ, documentation\n3. **Coordonnez les equipes** — Marketing, sales, support doivent etre alignes\n4. **Definissez les metriques de succes** — Sign-ups, demos, MRR genere\n5. **Planifiez le post-lancement** — Le travail ne s'arrete pas au jour J\n6. **Faites un retex** — Documentez ce qui a marche et ce qui peut etre ameliore",
      },
      {
        type: 'in-product',
        title: 'Dans Feedback Orchestrator',
        content:
          "Creez une roadmap dediee a votre lancement dans le module **Roadmap**. Liez chaque jalon au module correspondant : contenu (Blog Manager), campagne (Campaigns), planning (Content Calendar). Utilisez la vue timeline pour visualiser l'ensemble du plan.",
      },
    ],
    linkedModule: {
      label: 'Roadmap',
      path: '/growth/roadmap',
      icon: 'Map',
    },
  },
  {
    slug: 'cross-module',
    title: 'Correler roadmap et modules Growth',
    duration: '3 min',
    glossaryTerms: ['roadmap', 'content-factory', 'campaigns'],
    sections: [
      {
        type: 'concept',
        title: 'Le concept',
        content:
          "La force d'une roadmap ne reside pas dans la planification seule, mais dans sa correlation avec les outils d'execution. Chaque jalon de la roadmap doit pointer vers le module ou l'action se deroule.\n\nExemple de correlation :\n- Jalon \"Rediger 10 articles SEO\" → Lien vers Blog Manager\n- Jalon \"Lancer campagne Q2\" → Lien vers Campaigns\n- Jalon \"Creer 20 posts LinkedIn\" → Lien vers Content Factory",
      },
      {
        type: 'why',
        title: "Pourquoi c'est important ?",
        content:
          "Une roadmap deconnectee des outils d'execution devient vite obsolete. Personne ne la met a jour, et elle perd sa valeur. En liant chaque jalon a un module, vous creez un pont entre la strategie et l'action.\n\nLes equipes qui utilisent des outils integres (roadmap + execution) sont 40% plus productives que celles qui utilisent des outils separes.",
      },
      {
        type: 'methodology',
        title: 'Comment faire ?',
        content:
          "1. **Pour chaque jalon, identifiez le module lie** — Content Factory, Blog Manager, Campaigns, etc.\n2. **Ajoutez le lien dans le jalon** — Un clic pour passer de la roadmap a l'execution\n3. **Mettez a jour les statuts en temps reel** — Quand un article est publie, marquez le jalon comme termine\n4. **Utilisez les categories** — Contenu, SEO, campagne, sales, referral, ops\n5. **Revoyez la roadmap avec les metriques** — Le Marketing Dashboard montre l'impact de vos actions",
      },
      {
        type: 'in-product',
        title: 'Dans Feedback Orchestrator',
        content:
          "Lors de la creation d'un jalon dans la **Roadmap**, selectionnez le module lie dans le champ dedie. Le jalon affichera un lien cliquable vers la page correspondante. Vous pouvez aussi lier un jalon a une entite specifique (un article de blog, une campagne).",
      },
    ],
    linkedModule: {
      label: 'Roadmap',
      path: '/growth/roadmap',
      icon: 'Map',
    },
  },
  {
    slug: 'your-roadmap',
    title: 'La Roadmap Interactive dans FO',
    duration: '3 min',
    glossaryTerms: ['roadmap', 'milestone'],
    sections: [
      {
        type: 'concept',
        title: 'Le concept',
        content:
          "La Roadmap Interactive de Feedback Orchestrator est votre outil de planification operationnelle. Elle vous permet de creer des feuilles de route, d'ajouter des jalons, de les organiser par categorie et de suivre la progression en temps reel.",
      },
      {
        type: 'methodology',
        title: 'Comment l\'utiliser ?',
        content:
          "1. **Creez une roadmap** — Donnez-lui un titre, une date de debut et une date de fin\n2. **Ajoutez des jalons** — Titre, categorie, date limite, responsable, module lie\n3. **Organisez par priorite** — Glissez-deposez pour reordonner les jalons\n4. **Basculez entre les vues** — Timeline (vision temporelle) ou Kanban (vision par statut)\n5. **Mettez a jour regulierement** — Changez les statuts : planifie, en cours, termine, bloque\n6. **Mesurez la progression** — La barre de progression globale vous montre ou vous en etes",
      },
      {
        type: 'in-product',
        title: 'Dans Feedback Orchestrator',
        content:
          "Rendez-vous sur la page **Roadmap** depuis le menu lateral. Creez votre premiere feuille de route, ajoutez vos jalons et commencez a planifier votre strategie marketing. Chaque jalon peut etre lie a un module Growth pour passer facilement de la planification a l'action.",
      },
    ],
    linkedModule: {
      label: 'Roadmap',
      path: '/growth/roadmap',
      icon: 'Map',
    },
  },
];
